import { create } from 'zustand'

/** Lo que se le muestra a la persona antes de hacer algo que no se deshace. */
export interface Confirmacion {
  titulo: string

  /** El párrafo de abajo: qué pasa y qué NO pasa. Opcional. */
  detalle?: string

  /** El texto del botón que hace la cosa. «Dar de baja», «Borrar». */
  confirmar: string

  /**
   * Si la acción es destructiva, para pintar el botón en rojo.
   *
   * Va como bandera y no se deduce del texto: «Usar este dispositivo» no
   * destruye nada y también hace falta confirmarla.
   */
  peligrosa?: boolean

  /**
   * Un aviso: un solo botón y nada que decidir.
   *
   * Es lo que reemplaza al `alert()` del navegador, que estaba en dieciocho
   * lugares mostrando errores del servidor. Va acá y no en otro componente
   * porque es el mismo diálogo con un botón menos, y dos diálogos parecidos
   * terminan divergiendo en el estilo.
   */
  soloAviso?: boolean

  /**
   * Que el foco arranque en el botón que CONFIRMA, no en Cancelar.
   *
   * El default es al revés a propósito: casi todo lo que se confirma en el
   * sistema es una baja, y con gente esperando en el mostrador un Enter de más
   * no puede borrar nada. La excepción es el cobro: ahí confirmar no destruye
   * nada, es el paso que la persona está completando, y el sentido del diálogo
   * es que el cajero LEA el importe y el medio antes de que la plata entre al
   * arqueo. Pedirle que además mueva la mano al mouse convertiría el control en
   * un trámite y terminaría en «confirmar sin mirar».
   *
   * Nunca en algo destructivo. `peligrosa` y esto juntos es una contradicción.
   */
  enfocarConfirmar?: boolean
}

interface Pedido extends Confirmacion {
  responder: (respuesta: boolean) => void
}

interface Estado {
  pedido: Pedido | null
  pedir: (p: Pedido) => void
  responder: (respuesta: boolean) => void
}

/**
 * El diálogo de confirmación del sistema, con API imperativa.
 *
 * Reemplaza al `confirm()` del navegador, que funcionaba y era accesible pero
 * se ve como una alerta de 1998 en medio de un sistema con su propio estilo
 * —y en el mostrador, donde la pantalla es táctil, sus botones son diminutos—.
 *
 * Es imperativo y no un componente por pantalla a propósito: hay quince
 * lugares que confirman, casi todos dentro de una acción de fila de una
 * grilla, y hacerlo declarativo obligaba a que cada pantalla se guardara «qué
 * fila estoy por borrar» en su propio estado. Con esto, cada lugar cambia de
 * `if (confirm(...))` a `if (await confirmar(...))` y nada más.
 */
export const useConfirmacion = create<Estado>((set, get) => ({
  pedido: null,

  pedir: (p) => {
    // Si ya había uno abierto se lo cancela antes de pisarlo: su promesa tiene
    // que resolverse igual, o el `await` del que la pidió no vuelve nunca y la
    // pantalla queda esperando para siempre.
    get().pedido?.responder(false)

    set({ pedido: p })
  },

  responder: (respuesta) => {
    const { pedido } = get()
    if (!pedido) return

    // Se limpia ANTES de responder: el que espera puede abrir otra
    // confirmación en su continuación, y si el estado todavía tuviera la
    // anterior, `pedir` la cancelaría.
    set({ pedido: null })
    pedido.responder(respuesta)
  },
}))

/**
 * Pregunta y devuelve si dijo que sí.
 *
 * Nunca rechaza: cerrar el diálogo con Escape, con el fondo o con Cancelar
 * devuelve `false`, igual que el `confirm()` del navegador. Eso es lo que
 * permite escribir `if (!(await confirmar(...))) return` sin un try.
 */
export function confirmar(opciones: Confirmacion): Promise<boolean> {
  return new Promise((resolver) => {
    useConfirmacion.getState().pedir({ ...opciones, responder: resolver })
  })
}

/**
 * Avisa algo y espera el «Entendido».
 *
 * Reemplaza al `alert()` del navegador. La mayoría de los errores de un
 * formulario se pintan en su campo; esto es para los que no tienen campo —el
 * mensaje que manda el servidor cuando la operación entera no se pudo hacer— y
 * para los avisos del sistema, como que el navegador no dejó imprimir.
 *
 * Devuelve una promesa por simetría con `confirmar`, para poder encadenar algo
 * después de que la persona lo leyó; en general no se espera.
 */
export function avisar(opciones: { titulo: string; detalle?: string }): Promise<void> {
  return new Promise((resolver) => {
    useConfirmacion.getState().pedir({
      ...opciones,
      confirmar: 'Entendido',
      soloAviso: true,
      responder: () => resolver(),
    })
  })
}
